import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { CheckCircle, Copy, Loader2, AlertCircle, ExternalLink, FlaskConical, ArrowRight } from 'lucide-react';

const isDev = import.meta.env.DEV;

type ActivationState = 'loading' | 'show-terms' | 'activating' | 'success' | 'already-activated' | 'error';

interface ActivationResult {
  referralCode: string;
  referralLink: string;
}

const ActivatePartnerEarnings = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [state, setState] = useState<ActivationState>('loading');
  const [error, setError] = useState<string>('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [result, setResult] = useState<ActivationResult | null>(null);
  const [professionalName, setProfessionalName] = useState<string>('');
  const [generatingTest, setGeneratingTest] = useState(false);

  useEffect(() => {
    if (!token) {
      // No token - show test mode UI if in dev
      if (isDev) {
        setState('error');
        setError('No activation token provided. Use the "Generate Test Token" button below to test the flow.');
      } else {
        setState('error');
        setError('No activation token provided. Please use the link from your email.');
      }
      return;
    }

    // Decode token to get professional info (without verifying - that happens server-side)
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setProfessionalName(payload.name || 'Partner');
      
      // Check if token is expired client-side for better UX
      if (payload.exp && Date.now() > payload.exp * 1000) {
        setState('error');
        setError('This activation link has expired. Please contact support for a new link.');
        return;
      }
      
      setState('show-terms');
    } catch {
      setState('error');
      setError('Invalid activation token. Please use the link from your email.');
    }
  }, [token]);

  const handleGenerateTestToken = async () => {
    setGeneratingTest(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('generate-test-token');
      
      if (fnError) throw fnError;
      if (data.error) throw new Error(data.error);
      
      toast.success('Test data created! Redirecting...');
      
      // Navigate to the activation URL
      const url = new URL(data.activation_url);
      navigate(`/activate-partner-earnings?token=${url.searchParams.get('token')}`);
    } catch (err: any) {
      console.error('Generate test token error:', err);
      toast.error(err.message || 'Failed to generate test token');
    } finally {
      setGeneratingTest(false);
    }
  };

  const handleActivate = async () => {
    if (!termsAccepted) {
      toast.error('Please accept the terms to continue');
      return;
    }

    setState('activating');

    try {
      const { data, error: fnError } = await supabase.functions.invoke('accept-partner-terms', {
        body: { token }
      });

      if (fnError) throw fnError;

      if (data.error) {
        if (data.error.includes('already activated') || data.error.includes('Already activated')) {
          setState('already-activated');
          setResult({
            referralCode: data.referralCode || '',
            referralLink: data.referralLink || ''
          });
        } else {
          throw new Error(data.error);
        }
        return;
      }

      setState('success');
      setResult({
        referralCode: data.referralCode,
        referralLink: data.referralLink
      });
      toast.success('Your partner earnings are now activated!');
    } catch (err: any) {
      setState('error');
      setError(err.message || 'Failed to activate. Please try again or contact support.');
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard!');
    } catch {
      toast.error('Failed to copy');
    }
  };

  // Dev-only test button component
  const DevTestButton = () => {
    if (!isDev) return null;
    
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <Button 
          onClick={handleGenerateTestToken}
          disabled={generatingTest}
          variant="outline"
          className="bg-amber-100 border-amber-400 text-amber-800 hover:bg-amber-200"
        >
          {generatingTest ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <FlaskConical className="mr-2 h-4 w-4" />
              Generate Test Token
            </>
          )}
        </Button>
      </div>
    );
  };

  // Loading state
  if (state === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-card flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Validating your activation link...</p>
        </div>
        <DevTestButton />
      </div>
    );
  }

  // Error state
  if (state === 'error') {
    return (
      <>
        <SEO
          title="Activation Error - SeniorThrive Partner Program"
          description="There was an issue with your partner activation."
          noindex
        />
        <div className="min-h-screen bg-gradient-card py-16">
          <div className="container mx-auto px-4 max-w-2xl">
            <Card className="border-destructive/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-destructive">
                  <AlertCircle className="h-6 w-6" />
                  Activation Error
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Alert variant="destructive" className="mb-6">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
                <p className="text-muted-foreground mb-4">
                  If you continue to have issues, please contact our team at{' '}
                  <a href="mailto:pros@seniorthrive.com" className="text-primary underline">
                    pros@seniorthrive.com
                  </a>
                </p>
                <Button asChild>
                  <Link to="/partners">Back to Partners Page</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
        <DevTestButton />
      </>
    );
  }

  // Success or Already Activated state
  if (state === 'success' || state === 'already-activated') {
    return (
      <>
        <SEO
          title="Partner Activated - SeniorThrive"
          description="Your partner referral link is ready to use."
          noindex
        />
        <div className="min-h-screen bg-gradient-card py-16">
          <div className="container mx-auto px-4 max-w-2xl">
            <Card className="border-primary/20">
              <CardHeader className="text-center">
                <div className="mx-auto mb-4 h-16 w-16 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-10 w-10 text-primary" />
                </div>
                <CardTitle className="text-2xl">
                  {state === 'already-activated' ? 'Already Activated!' : 'You\'re All Set!'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-center text-muted-foreground">
                  {state === 'already-activated' 
                    ? 'Your partner earnings were previously activated. Here\'s your referral link:'
                    : 'Your partner earnings are now active. Share your unique referral link to start earning!'}
                </p>

                {result && (
                  <div className="space-y-4">
                    <div className="bg-muted/50 rounded-lg p-4">
                      <label className="text-sm font-medium text-muted-foreground block mb-2">
                        Your Referral Code
                      </label>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-background px-3 py-2 rounded border text-sm font-mono">
                          {result.referralCode}
                        </code>
                        <Button 
                          size="icon" 
                          variant="outline"
                          onClick={() => copyToClipboard(result.referralCode)}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="bg-muted/50 rounded-lg p-4">
                      <label className="text-sm font-medium text-muted-foreground block mb-2">
                        Your Referral Link
                      </label>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-background px-3 py-2 rounded border text-sm break-all">
                          {result.referralLink}
                        </code>
                        <Button 
                          size="icon" 
                          variant="outline"
                          onClick={() => copyToClipboard(result.referralLink)}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Primary CTA - What's Next */}
                <div className="rounded-lg p-5 mb-4">
                  <p className="text-sm text-muted-foreground mb-3">
                    {state === 'success' 
                      ? "Ready to start sharing? We've put together everything you need."
                      : "Need a refresher on how to share SeniorThrive?"}
                  </p>
                  <Button asChild size="lg" className="w-full bg-gradient-cta hover:opacity-90">
                    <Link to={`/partner-getting-started?code=${result?.referralCode || ''}&link=${encodeURIComponent(result?.referralLink || '')}`}>
                      {state === 'success' ? "See What's Next" : "View Getting Started Guide"}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <Button asChild variant={state === 'success' ? 'outline' : 'default'} className="flex-1">
                    <Link to="/affiliate">
                      View Affiliate Portal
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="flex-1">
                    <Link to="/partners">Back to Partners</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        <DevTestButton />
      </>
    );
  }

  // Show Terms state (default)
  return (
    <>
      <SEO
        title="Activate Partner Earnings - SeniorThrive"
        description="Accept the partner terms to activate your referral earnings."
        noindex
      />
      <div className="min-h-screen bg-gradient-card py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-3xl">
                Welcome, {professionalName}!
              </CardTitle>
              <p className="text-muted-foreground mt-2">
                Review and accept the partner terms to activate your referral earnings.
              </p>
            </CardHeader>
            <CardContent>
              {/* Terms Content */}
              <div className="bg-muted/30 rounded-xl p-6 md:p-8 mb-8 max-h-[400px] overflow-y-auto">
                <h2 className="text-2xl font-bold text-foreground mb-2">
                  SeniorThrive Partner Referral Program Terms
                </h2>
                <p className="text-sm text-muted-foreground mb-6">Effective Date: December 2025</p>
                
                <div className="space-y-6">
                  <section>
                    <h3 className="text-base font-bold text-foreground mb-2">1. Program Overview</h3>
                    <p className="text-sm text-muted-foreground">
                      The SeniorThrive Partner Referral Program allows approved members of the SeniorThrive Professional Network ("Partners") to refer new customers to SeniorThrive and earn referral rewards. Participation is optional. By activating your referral link or participating in the program, you agree to these terms.
                    </p>
                  </section>

                  <section>
                    <h3 className="text-base font-bold text-foreground mb-2">2. Eligibility</h3>
                    <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
                      <li>Only verified SeniorThrive Professional Network members who have been issued an official referral link may participate.</li>
                      <li>Participation and rewards are subject to SeniorThrive approval at all times.</li>
                      <li>SeniorThrive may approve, deny, suspend, or revoke participation at its discretion.</li>
                    </ul>
                  </section>

                  <section>
                    <h3 className="text-base font-bold text-foreground mb-2">3. How Referrals Work</h3>
                    <p className="text-sm text-muted-foreground mb-2">Each Partner receives a unique referral link. A referral is credited only when:</p>
                    <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1 mb-2">
                      <li>A new user signs up through your referral link, and</li>
                      <li>That user completes a qualifying paid purchase of Essential or Family.</li>
                    </ul>
                    <p className="text-sm text-muted-foreground mb-2">Referrals are not credited for:</p>
                    <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
                      <li>Existing or prior customers</li>
                      <li>Self-referrals</li>
                      <li>Offline referrals</li>
                      <li>Referrals not completed through your unique link</li>
                    </ul>
                    <p className="text-sm text-muted-foreground mt-2">SeniorThrive determines referral eligibility in its sole discretion.</p>
                  </section>

                  <section>
                    <h3 className="text-base font-bold text-foreground mb-2">4. Rewards and Payouts</h3>
                    <p className="text-sm text-muted-foreground font-medium mb-2">Partner Referral Earnings:</p>
                    <div className="overflow-x-auto mb-4">
                      <table className="w-full text-sm text-muted-foreground border-collapse">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-2 pr-4 font-medium">Plan</th>
                            <th className="text-left py-2 pr-4 font-medium">Commission</th>
                            <th className="text-left py-2 pr-4 font-medium">Frequency</th>
                            <th className="text-left py-2 font-medium">Duration</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-border/50">
                            <td className="py-2 pr-4">Essential</td>
                            <td className="py-2 pr-4">20% of net subscription revenue</td>
                            <td className="py-2 pr-4">Monthly</td>
                            <td className="py-2">Up to 12 months</td>
                          </tr>
                          <tr>
                            <td className="py-2 pr-4">Family</td>
                            <td className="py-2 pr-4">25% of net subscription revenue</td>
                            <td className="py-2 pr-4">Monthly</td>
                            <td className="py-2">Up to 12 months</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">Examples (for clarity only):</p>
                    <ul className="text-xs text-muted-foreground list-disc pl-5 space-y-1 mb-3">
                      <li>An Essential subscriber at $29/month may generate approximately $5.80 per month, paid monthly, for up to 12 months.</li>
                      <li>A Family subscriber at $59/month may generate approximately $14.75 per month, paid monthly, for up to 12 months.</li>
                    </ul>
                    <p className="text-xs text-muted-foreground italic mb-3">Actual earnings depend on the amount paid and may vary.</p>
                    
                    <p className="text-sm text-muted-foreground font-medium mb-2">Important Payment Details:</p>
                    <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1 mb-3">
                      <li>Earnings accrue monthly while the referred account remains active and in good standing.</li>
                      <li>Commissions are calculated on net subscription revenue, meaning amounts actually received by SeniorThrive after: refunds, chargebacks, taxes, credits, and payment processing fees.</li>
                      <li>Earnings are not paid instantly.</li>
                    </ul>
                    
                    <p className="text-sm text-muted-foreground font-medium mb-2">Payout Schedule:</p>
                    <p className="text-sm text-muted-foreground mb-2">Payouts are issued when:</p>
                    <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
                      <li>Your accrued balance reaches $50, or</li>
                      <li>Six (6) months have passed since your last payout, whichever comes first.</li>
                    </ul>
                    <p className="text-sm text-muted-foreground mt-2">Payouts are issued using the payment method on file. You may be required to provide payment and tax information before payouts are issued.</p>
                  </section>

                  <section>
                    <h3 className="text-base font-bold text-foreground mb-2">5. Validation, Adjustments, and Fraud Prevention</h3>
                    <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
                      <li>Referred accounts must pass a standard validation period.</li>
                      <li>Accounts that are canceled, refunded, disputed, or flagged for fraud are not eligible for rewards.</li>
                      <li>SeniorThrive reserves the right to audit referrals and payouts, adjust or withhold payments, and suspend or terminate participation for misuse or policy violations.</li>
                      <li>All decisions regarding referral eligibility and payouts are final.</li>
                    </ul>
                  </section>

                  <section>
                    <h3 className="text-base font-bold text-foreground mb-2">6. Sharing Your Referral Link</h3>
                    <p className="text-sm text-muted-foreground font-medium mb-2">Permitted:</p>
                    <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1 mb-3">
                      <li>Sharing with your own authentic contacts, such as clients, families, and colleagues</li>
                      <li>Educational or informational sharing consistent with your professional role</li>
                    </ul>
                    <p className="text-sm text-muted-foreground font-medium mb-2">Not Permitted:</p>
                    <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
                      <li>Spam or unsolicited bulk messaging</li>
                      <li>Paid advertising using the referral link</li>
                      <li>Misrepresenting SeniorThrive or impersonating the company</li>
                      <li>Making medical claims, guarantees, or misleading statements</li>
                    </ul>
                    <p className="text-sm text-muted-foreground mt-2">Violations may result in suspension or termination of referral privileges.</p>
                  </section>

                  <section>
                    <h3 className="text-base font-bold text-foreground mb-2">7. Program Changes, Suspension, and Termination</h3>
                    <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
                      <li>You may opt out of the program at any time.</li>
                      <li>SeniorThrive may suspend or terminate participation for violations of these terms, for suspected misuse or fraud, or at its discretion.</li>
                      <li>SeniorThrive may modify, pause, or end the program with reasonable notice of material changes.</li>
                      <li>Approved earnings accrued prior to termination will be paid on the normal payout cycle unless disqualified under these terms.</li>
                    </ul>
                  </section>

                  <section>
                    <h3 className="text-base font-bold text-foreground mb-2">8. Taxes and Compliance</h3>
                    <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
                      <li>You are responsible for all taxes related to referral earnings.</li>
                      <li>SeniorThrive may require tax documentation as required by law.</li>
                      <li>Failure to provide required documentation may delay or prevent payouts.</li>
                    </ul>
                  </section>

                  <section>
                    <h3 className="text-base font-bold text-foreground mb-2">9. Independent Contractor Relationship</h3>
                    <p className="text-sm text-muted-foreground">
                      Participation in the referral program does not create an employment, agency, partnership, or joint venture relationship. Partners may not bind or represent SeniorThrive.
                    </p>
                  </section>

                  <section>
                    <h3 className="text-base font-bold text-foreground mb-2">10. Limitation of Liability</h3>
                    <p className="text-sm text-muted-foreground">
                      To the fullest extent permitted by law: SeniorThrive is not liable for indirect, incidental, or consequential damages related to the referral program. Referral rewards are not guaranteed.
                    </p>
                  </section>

                  <section>
                    <h3 className="text-base font-bold text-foreground mb-2">11. Governing Law</h3>
                    <p className="text-sm text-muted-foreground">
                      These terms are governed by the laws of the State of California. Any disputes are subject to the courts located in Orange County, California.
                    </p>
                  </section>

                  <section>
                    <h3 className="text-base font-bold text-foreground mb-2">12. Acceptance</h3>
                    <p className="text-sm text-muted-foreground">
                      Using your Partner Referral Link or participating in the Partner Referral Program constitutes acceptance of these terms.
                    </p>
                  </section>
                </div>
              </div>

              {/* Terms Checkbox */}
              <div className="flex items-start gap-3 mb-6 p-4 rounded-lg">
                <Checkbox 
                  id="terms" 
                  checked={termsAccepted}
                  onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                  className="mt-1"
                />
                <label htmlFor="terms" className="text-sm cursor-pointer">
                  I have read, understood, and agree to the Partner Referral Program Terms. I understand that my referral earnings will be tracked and paid according to the program guidelines.
                </label>
              </div>

              {/* Action Button */}
              <Button 
                onClick={handleActivate} 
                disabled={!termsAccepted || state === 'activating'}
                className="w-full"
                size="lg"
              >
                {state === 'activating' ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Activating...
                  </>
                ) : (
                  'Accept Terms & Activate My Referral Link'
                )}
              </Button>

              <p className="text-center text-xs text-muted-foreground mt-4">
                By activating, you also agree to our{' '}
                <Link to="/terms" className="underline">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" className="underline">Privacy Policy</Link>.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
      <DevTestButton />
    </>
  );
};

export default ActivatePartnerEarnings;
